import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { useAddEmailOrganizationMutation } from 'services/organizations'
import { useLogoutMutation } from 'services/auth/logout'
import { useDispatch } from 'react-redux'
import { usersApi } from 'services/users'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { TFunction } from 'i18next'

interface Props {
  open: boolean
  onClose: () => void
}

function createSchema(t: TFunction) {
  return z.object({
    email: z
      .string()
      .email({ message: t('invalidEmail') })
      .min(5, {
        message: t('required'),
      }),
  })
}

export function OrganizationEmailDialog({ open, onClose }: Props) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const validationSchema = createSchema(t)

  const [logout] = useLogoutMutation()
  const [addOrgEmail] = useAddEmailOrganizationMutation()

  const handleClose = () => {
    onClose()
    logout()
  }

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<{ email: string }>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(validationSchema),
  })

  const onSubmit = async (values: { email: string }) => {
    await addOrgEmail(values).then((d) => {
      if (!('error' in d)) {
        onClose()
        dispatch(usersApi.util.invalidateTags(['UserCurrent']))
        // window.location.reload()
        navigate('/portal')
      }
      if ('error' in d) {
        handleClose()
      }
    })
  }

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={(event: Event, reason: string) => {
        if (reason === 'backdropClick') {
          return false
        }
        onClose()
      }}
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ fontWeight: 'bold' }}>{t('orgEmailAddress')}</DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mt={1}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Box>
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={t('org-email-address')}
                    size="small"
                    variant="outlined"
                  />
                  {errors.email && (
                    <FormHelperText error>{errors.email?.message}</FormHelperText>
                  )}
                </Box>
              )}
            />
          </Box>

          <Box display="flex" mt={2} justifyContent="flex-end" alignItems="center">
            <Button
              type="submit"
              variant="contained"
              style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            >
              {t('confirm')}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  )
}
